import axios from 'axios';
import { AxiosError } from 'axios';
import { http } from '../http';

export type UserType = 'CONSULTOR' | 'TI' | 'ADMIN';

export interface SignupPayload {
  email: string;
  password: string;
  confirmPassword: string;
  volunteerCode: string;
  cpfCnpj: string;
  nomeCompleto: string;
  celular: string;

  // `isAdmin` saiu daqui na at-68: a flag é legada desde 15/07/2026 (decisão registrada
  // em `01-identity.md`) e quem define papel é `userType`. O DTO do backend ainda a
  // aceita; o que mudou é que a interface parou de mandá-la.
  userType?: UserType;

  codigo_regionais?: string[];
  codigo_cooperativas?: string[];
}

export interface SignupResponse {
  message: string;
  /** at-68: a central de usuários abre a ficha do usuário recém-criado. */
  id?: string;
  accessToken?: string;
  firstLogin?: boolean;
}

/** Um resultado por linha do CSV — o backend nunca aborta o arquivo inteiro. */
export interface ResultadoImportacaoCsv {
  email?: string;
  sucesso: boolean;
  erro?: string;
}

export interface LoginResponse {
  accessToken: string;
  firstLogin: boolean;
}

interface ChangeFirstPasswordPayload {
  newPassword: string;
  confirmNewPassword: string;
}

export interface TiResetPasswordPayload {
  targetUserId: string;
  newPassword: string;
  confirmNewPassword: string;
}

export interface TiResetPasswordResponse {
  message?: string;
}

// Rotas públicas de auth (signup/login/refresh/logout) usam axios "cru": não
// devem passar pelo interceptor do `http`, para que um 401 no login não dispare
// o logout automático. A criação de usuário TI/ADMIN precisa mandar o cookie de
// sessão junto (withCredentials).
export const signupUser = async (payload: SignupPayload): Promise<SignupResponse> => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/auth/signup`,
      {
        ...payload,
        userType: payload.userType ?? 'CONSULTOR',
        codigo_regionais: payload.codigo_regionais ?? [],
        codigo_cooperativas: payload.codigo_cooperativas ?? [],
      },
      { withCredentials: true }
    );

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao cadastrar usuário');
    }
    throw new Error('Erro desconhecido');
  }
};

export const loginUser = async (email: string, password: string): Promise<LoginResponse> => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/auth/login`,
      { email, password },
      { withCredentials: true }
    );

    // O `refreshToken` do corpo é deliberadamente ignorado (at-22): ele já vem
    // em cookie httpOnly e guardar uma cópia no cliente só expõe o segredo.
    const { accessToken, firstLogin } = response.data;

    return { accessToken, firstLogin };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao fazer login');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

// O `refreshToken()` que existia aqui foi removido na at-22: era um TERCEIRO
// caminho de renovação, sem nenhum import, que só convidava a próxima
// manutenção a criar mais uma corrida. A renovação da aplicação é única e vive
// em `refreshOnce` (src/api/http.ts).

export const logoutUser = async (): Promise<void> => {
  try {
    await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/auth/logout`,
      {},
      {
        withCredentials: true,
      }
    );
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao fazer logout');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const changeFirstLoginPassword = async (
  payload: ChangeFirstPasswordPayload
): Promise<{ message: string }> => {
  try {
    const response = await http.put('/auth/first-login-password', payload);

    return response.data;
  } catch (error: unknown) {
    const axiosError = error as AxiosError;
    throw axiosError;
  }
};

/**
 * Importação em massa de usuários por CSV (at-68).
 *
 * Passa pelo cliente `http` (Bearer injetado): a rota deixou de ser pública nesta
 * entrega — antes qualquer pessoa na internet criava usuários com a senha temporária
 * conhecida. Todos nascem CONSULTOR e com primeiro acesso obrigatório.
 */
export const importarUsuariosCsv = async (
  arquivo: File
): Promise<ResultadoImportacaoCsv[]> => {
  const corpo = new FormData();
  corpo.append('file', arquivo);

  try {
    // Sem `Content-Type` explícito: o navegador precisa montar o boundary do multipart.
    const response = await http.post('/auth/signup-csv', corpo);

    return response.data;
  } catch (error: unknown) {
    const axiosError = error as AxiosError<any>;

    throw new Error(
      axiosError.response?.data?.message || 'Erro ao importar os usuários do arquivo'
    );
  }
};

export const tiResetPassword = async (
  payload: TiResetPasswordPayload
): Promise<TiResetPasswordResponse> => {
  try {
    const response = await http.post('/auth/ti/reset-password', payload);

    return response.data;
  } catch (error: unknown) {
    const axiosError = error as AxiosError<any>;

    throw new Error(
      axiosError.response?.data?.message ||
        'Erro ao resetar senha do usuário'
    );
  }
};
